import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calendar, Copy, Edit, History, Plus, RefreshCw, Percent } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import SearchBar from '@/components/shared/SearchBar';
import { API, toast } from '@/App';

const EMPTY_FORM = {
  code: '',
  label: '',
  description: '',
  discount_type: 'percent',
  discount_value: 0,
  applicable_target: 'both',
  scope_type: 'global',
  country_scope_text: '',
  city_scope_text: '',
  stacking_policy: 'not_stackable',
  starts_at: '',
  expires_at: '',
  max_total_uses: '',
  max_uses_per_user: 1,
  is_active: true,
};

const parseListInput = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim().toUpperCase())
  .filter(Boolean);

const toDateTimeLocal = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (input) => String(input).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatMoney = (amount, currency = 'FCFA') => `${Number(amount || 0).toLocaleString('fr-FR')} ${currency}`;

const resolveApplicableTarget = (promo) => {
  const targets = Array.isArray(promo?.applicable_to) ? promo.applicable_to : [];
  if (targets.includes('subscription') && targets.includes('delivery')) {
    return 'both';
  }
  if (targets.includes('delivery')) {
    return 'delivery';
  }
  return 'subscription';
};

const buildPromoPayload = (formData) => ({
  code: String(formData.code || '').trim().toUpperCase(),
  label: String(formData.label || '').trim() || undefined,
  description: String(formData.description || '').trim() || undefined,
  discount_type: formData.discount_type,
  discount_value: Number(formData.discount_value || 0),
  applicable_to: formData.applicable_target === 'both'
    ? ['subscription', 'delivery']
    : [formData.applicable_target],
  scope_type: formData.scope_type,
  country_scope: parseListInput(formData.country_scope_text),
  city_scope: parseListInput(formData.city_scope_text),
  stacking_policy: formData.stacking_policy,
  starts_at: formData.starts_at ? new Date(formData.starts_at).toISOString() : null,
  expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
  max_total_uses: formData.max_total_uses ? Number(formData.max_total_uses) : null,
  max_uses_per_user: Number(formData.max_uses_per_user || 1),
  is_active: Boolean(formData.is_active),
});

const mapPromoToForm = (promo) => ({
  code: promo.code || '',
  label: promo.label || '',
  description: promo.description || '',
  discount_type: promo.discount_type || 'percent',
  discount_value: Number(promo.discount_value || 0),
  applicable_target: resolveApplicableTarget(promo),
  scope_type: promo.scope_type || 'global',
  country_scope_text: (promo.country_scope || []).join(', '),
  city_scope_text: (promo.city_scope || []).join(', '),
  stacking_policy: promo.stacking_policy || 'not_stackable',
  starts_at: toDateTimeLocal(promo.starts_at),
  expires_at: toDateTimeLocal(promo.expires_at),
  max_total_uses: promo.max_total_uses ?? '',
  max_uses_per_user: promo.max_uses_per_user ?? 1,
  is_active: Boolean(promo.is_active),
});

const statusBadge = (statusBucket) => {
  const normalized = String(statusBucket || '').toLowerCase();
  if (normalized === 'active') return <Badge className="bg-green-100 text-green-700">Actif</Badge>;
  if (normalized === 'scheduled') return <Badge className="bg-blue-100 text-blue-700">Planifie</Badge>;
  if (normalized === 'expired') return <Badge className="bg-gray-200 text-gray-700">Expire</Badge>;
  if (normalized === 'confirmed') return <Badge className="bg-green-100 text-green-700">Confirme</Badge>;
  if (normalized === 'reserved') return <Badge className="bg-amber-100 text-amber-700">Reserve</Badge>;
  if (normalized === 'cancelled') return <Badge className="bg-gray-200 text-gray-700">Annule</Badge>;
  return <Badge className="bg-amber-100 text-amber-700">Inactif</Badge>;
};

const PromoCodeManagement = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoUsages, setPromoUsages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const loadPromoData = async () => {
    setLoading(true);
    try {
      const [codesResponse, usagesResponse] = await Promise.all([
        axios.get(`${API}/admin/promo-codes`),
        axios.get(`${API}/admin/promo-code-usages`, { params: { limit: 100 } }),
      ]);
      setPromoCodes(codesResponse.data || []);
      setPromoUsages(usagesResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du chargement des codes promo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPromoData();
  }, []);

  const filteredPromoCodes = useMemo(() => {
    const normalizedQuery = String(searchQuery || '').trim().toLowerCase();
    if (!normalizedQuery) {
      return promoCodes;
    }
    return promoCodes.filter((promo) => (
      String(promo.code || '').toLowerCase().includes(normalizedQuery)
      || String(promo.label || '').toLowerCase().includes(normalizedQuery)
      || String(promo.description || '').toLowerCase().includes(normalizedQuery)
    ));
  }, [promoCodes, searchQuery]);

  const summary = useMemo(() => {
    const active = promoCodes.filter((promo) => promo.status_bucket === 'active').length;
    const scheduled = promoCodes.filter((promo) => promo.status_bucket === 'scheduled').length;
    const expired = promoCodes.filter((promo) => promo.status_bucket === 'expired').length;
    const totalConfirmedDiscount = promoCodes.reduce(
      (accumulator, promo) => accumulator + Number(promo.usage_stats?.discount_total_confirmed || 0),
      0,
    );
    return { active, scheduled, expired, totalConfirmedDiscount };
  }, [promoCodes]);

  const filteredUsages = useMemo(() => {
    const normalizedQuery = String(searchQuery || '').trim().toLowerCase();
    if (!normalizedQuery) {
      return promoUsages;
    }
    return promoUsages.filter((usage) => (
      String(usage.promo?.code || usage.code || '').toLowerCase().includes(normalizedQuery)
      || String(usage.user?.full_name || '').toLowerCase().includes(normalizedQuery)
      || String(usage.user?.email || '').toLowerCase().includes(normalizedQuery)
    ));
  }, [promoUsages, searchQuery]);

  const openCreateDialog = () => {
    setEditingPromo(null);
    setFormData(EMPTY_FORM);
    setShowDialog(true);
  };

  const openEditDialog = (promo) => {
    setEditingPromo(promo);
    setFormData(mapPromoToForm(promo));
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!String(formData.code || '').trim()) {
      toast.error('Le code promo est obligatoire.');
      return;
    }
    if (!formData.discount_value || Number(formData.discount_value) <= 0) {
      toast.error('La valeur de reduction doit etre superieure a zero.');
      return;
    }
    if (!formData.expires_at) {
      toast.error('La date de fin est obligatoire.');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPromoPayload(formData);
      if (editingPromo) {
        await axios.patch(`${API}/admin/promo-codes/${editingPromo.id}`, payload);
        toast.success('Code promo mis a jour.');
      } else {
        await axios.post(`${API}/admin/promo-codes`, payload);
        toast.success('Code promo cree.');
      }
      setShowDialog(false);
      await loadPromoData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible d enregistrer ce code promo');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (promo) => {
    try {
      await axios.patch(`${API}/admin/promo-codes/${promo.id}`, {
        is_active: !promo.is_active,
      });
      toast.success(!promo.is_active ? 'Code active.' : 'Code desactive.');
      await loadPromoData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de modifier ce code promo');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copie.');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Codes promo V2</h2>
          <p className="text-gray-600 mt-1">Pilotez les remises abonnement et livraison avec historique d usage et regles metier claires.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadPromoData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={openCreateDialog} className="bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600">
            <Plus className="w-4 h-4 mr-2" />
            Creer un code
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold gradient-text">{summary.active}</p>
            <p className="text-sm text-gray-600">Codes actifs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-600">{summary.scheduled}</p>
            <p className="text-sm text-gray-600">Planifies</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-gray-500">{summary.expired}</p>
            <p className="text-sm text-gray-600">Expires</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-green-700">{formatMoney(summary.totalConfirmedDiscount)}</p>
            <p className="text-sm text-gray-600">Remises confirmees</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Catalogue promo</CardTitle>
              <CardDescription>Chaque code reste pilote par le backend et historise dans les usages confirmes ou reserves.</CardDescription>
            </div>
            <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Rechercher un code, un label ou un utilisateur..." className="max-w-sm" />
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="codes">
            <TabsList>
              <TabsTrigger value="codes">Codes ({filteredPromoCodes.length})</TabsTrigger>
              <TabsTrigger value="usages">Historique ({filteredUsages.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="codes" className="mt-4">
              {filteredPromoCodes.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <Percent className="mx-auto mb-3 h-12 w-12 opacity-40" />
                  <p>Aucun code promo ne correspond a votre recherche.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Reduction</TableHead>
                      <TableHead>Cible</TableHead>
                      <TableHead>Utilisation</TableHead>
                      <TableHead>Validite</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPromoCodes.map((promo) => (
                      <TableRow key={promo.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold">{promo.code}</span>
                              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(promo.code)}>
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                            <p className="text-sm font-medium">{promo.label || promo.code}</p>
                            {promo.description && <p className="max-w-xs text-xs text-gray-500">{promo.description}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <Badge variant="outline">
                              {promo.discount_type === 'percent' ? `${promo.discount_value}%` : `${promo.discount_value} ${promo.usage_stats?.currency || 'FCFA'}`}
                            </Badge>
                            <p className="text-xs text-gray-500">Stacking: {promo.stacking_policy}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(promo.applicable_to || []).map((target) => (
                              <Badge key={target} variant="outline">{target === 'subscription' ? 'Abonnement' : 'Livraison'}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <p>{promo.usage_stats?.current_uses || 0}/{promo.max_total_uses ?? '∞'} confirmes</p>
                            <p className="text-xs text-gray-500">
                              Reserves: {promo.usage_stats?.reserved_uses || 0} | Par user: {promo.max_uses_per_user}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="space-y-1">
                            <p>{promo.starts_at ? new Date(promo.starts_at).toLocaleString('fr-FR') : 'Immediate'}</p>
                            <p className="text-gray-500">{promo.expires_at ? new Date(promo.expires_at).toLocaleString('fr-FR') : 'Sans expiration'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            {statusBadge(promo.status_bucket)}
                            <div className="flex items-center gap-2">
                              <Switch checked={promo.is_active} onCheckedChange={() => handleToggleActive(promo)} />
                              <span className="text-xs text-gray-500">{promo.is_active ? 'Visible' : 'Coupe'}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" onClick={() => openEditDialog(promo)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="usages" className="mt-4">
              {filteredUsages.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <History className="mx-auto mb-3 h-12 w-12 opacity-40" />
                  <p>Aucun usage enregistre pour le moment.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Contexte</TableHead>
                      <TableHead>Montants</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsages.map((usage) => (
                      <TableRow key={usage.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-mono font-semibold">{usage.promo?.code || usage.code}</p>
                            <p className="text-xs text-gray-500">{usage.promo?.label || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{usage.user?.full_name || usage.user_id}</p>
                            <p className="text-gray-500">{usage.user?.email || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{usage.context?.type === 'subscription' ? 'Abonnement' : 'Livraison'}</p>
                            <p className="text-gray-500">
                              {usage.context?.tracking_code || usage.context?.plan || usage.context_id}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <p>Brut: {formatMoney(usage.amount_before, usage.currency)}</p>
                          <p className="text-green-700">Promo: - {formatMoney(usage.discount_amount, usage.currency)}</p>
                          <p className="font-semibold">Net: {formatMoney(usage.amount_after, usage.currency)}</p>
                        </TableCell>
                        <TableCell>{statusBadge(usage.status === 'confirmed' ? 'active' : usage.status)}</TableCell>
                        <TableCell className="text-sm">
                          {new Date(usage.confirmed_at || usage.created_at).toLocaleString('fr-FR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingPromo ? 'Modifier' : 'Creer'} un code promo</DialogTitle>
            <DialogDescription>
              Configurez une remise compatible abonnement, livraison et Mobile Money sans sortir du calcul backend.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Code</Label>
              <Input value={formData.code} onChange={(event) => setFormData((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))} placeholder="PROMO20" />
            </div>
            <div>
              <Label>Label</Label>
              <Input value={formData.label} onChange={(event) => setFormData((prev) => ({ ...prev, label: event.target.value }))} placeholder="Offre lancement" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={3} value={formData.description} onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))} placeholder="Contexte interne ou message de campagne." />
            </div>
            <div>
              <Label>Type de reduction</Label>
              <Select value={formData.discount_type} onValueChange={(value) => setFormData((prev) => ({ ...prev, discount_type: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Pourcentage</SelectItem>
                  <SelectItem value="fixed">Montant fixe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valeur</Label>
              <Input type="number" min="0" step="0.01" value={formData.discount_value} onChange={(event) => setFormData((prev) => ({ ...prev, discount_value: event.target.value }))} />
            </div>
            <div>
              <Label>Cible</Label>
              <Select value={formData.applicable_target} onValueChange={(value) => setFormData((prev) => ({ ...prev, applicable_target: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="subscription">Abonnement</SelectItem>
                  <SelectItem value="delivery">Livraison</SelectItem>
                  <SelectItem value="both">Les deux</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stacking policy</Label>
              <Select value={formData.stacking_policy} onValueChange={(value) => setFormData((prev) => ({ ...prev, stacking_policy: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_stackable">Non cumulable</SelectItem>
                  <SelectItem value="best_discount_only">Meilleure remise</SelectItem>
                  <SelectItem value="stack_with_referral_cap">Cumul referral plafonne</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scope</Label>
              <Select value={formData.scope_type} onValueChange={(value) => setFormData((prev) => ({ ...prev, scope_type: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="special">Special</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pays (codes ISO, separes par virgule)</Label>
              <Input value={formData.country_scope_text} onChange={(event) => setFormData((prev) => ({ ...prev, country_scope_text: event.target.value }))} placeholder="CG, CD" />
            </div>
            <div>
              <Label>Villes (codes, separes par virgule)</Label>
              <Input value={formData.city_scope_text} onChange={(event) => setFormData((prev) => ({ ...prev, city_scope_text: event.target.value }))} placeholder="BZV, KIN" />
            </div>
            <div>
              <Label>Debut</Label>
              <Input type="datetime-local" value={formData.starts_at} onChange={(event) => setFormData((prev) => ({ ...prev, starts_at: event.target.value }))} />
            </div>
            <div>
              <Label>Fin</Label>
              <Input type="datetime-local" value={formData.expires_at} onChange={(event) => setFormData((prev) => ({ ...prev, expires_at: event.target.value }))} />
            </div>
            <div>
              <Label>Plafond total</Label>
              <Input type="number" min="0" value={formData.max_total_uses} onChange={(event) => setFormData((prev) => ({ ...prev, max_total_uses: event.target.value }))} placeholder="Laisser vide pour illimite" />
            </div>
            <div>
              <Label>Limite par utilisateur</Label>
              <Input type="number" min="1" value={formData.max_uses_per_user} onChange={(event) => setFormData((prev) => ({ ...prev, max_uses_per_user: event.target.value }))} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 md:col-span-2">
              <div>
                <p className="font-medium">Code actif</p>
                <p className="text-sm text-gray-500">Un code inactif reste historise mais n est plus applicable.</p>
              </div>
              <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_active: checked }))} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Enregistrement...' : editingPromo ? 'Mettre a jour' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoCodeManagement;
